import { useState, useEffect } from "react";
import {
  Box,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  Divider,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { tokens } from "../../theme"; // Adjust based on your theme configuration
import Header from "../../components/Header";
import { useTheme } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import axios from "axios";
import { CheckCircle, Cancel } from '@mui/icons-material';
import apiService from "../../service/apiService";

const Statedata = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);
  const [openDelete, setOpenDelete] = useState(false); // State for Delete dialog
  const [selectedRow, setSelectedRow] = useState(null);
  const [newState, setNewState] = useState({ stateName: "" });
  const [updatedBy, setUpdatedBy] = useState(""); // State for updatedBy input

  // Fetch data from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await apiService.get("/states/get-all");
        console.log("API response:", response.data);
        setRows(response.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  const handleDeleteClick = (row) => {
    setSelectedRow(row);
    setOpenDelete(true); // Open Delete dialog
  };

  const handleDelete = async () => {
    try {
      if (!selectedRow || !selectedRow.stateId) {
        console.error("No row selected for deletion.");
        return;
      }

      const { stateId } = selectedRow;

      // API call to delete the specific state by ID
      await apiService.delete(`/states/delete/${stateId}`);

      // Update the local rows state by filtering out the deleted row
      const updatedRows = rows.filter((row) => row.stateId !== stateId);
      setRows(updatedRows);
    } catch (error) {
      console.error("Error deleting data:", error);
    } finally {
      setOpenDelete(false);
    }
  };


  const handleEdit = (row) => {
    setSelectedRow(row);
    setOpenEdit(true);
  };

  const handleView = (row) => {
    setSelectedRow(row);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedRow(null);
  };

  const handleCloseEdit = () => {
    setOpenEdit(false);
    setSelectedRow(null);
  };

  const handleCloseAdd = () => {
    setOpenAdd(false);
    setNewState({ stateName: "", createdBy: "" });
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
    setUpdatedBy(""); // Reset updatedBy value
  };

  const handleSaveAdd = async () => {
    try {
      const response = await apiService.post("/states/create", {
        stateName: newState.stateName,
      });
      console.log("Create response:", response.data);
      setRows((prevRows) => [...prevRows, response.data]);
    } catch (error) {
      console.error("Error creating state:", error);
    } finally {
      handleCloseAdd();
    }
  };


  const handleSaveEdit = async () => {
    try {
      const { stateId, stateName } = selectedRow;
      const response = await apiService.put(`/states/update/${stateId}`, {
        stateName,

      });

      console.log("Update response:", response.data);

      const updatedRows = rows.map((row) =>
        row.stateId === stateId ? { ...row, stateName } : row
      );
      setRows(updatedRows);
    } catch (error) {
      console.error("Error updating data:", error);
    } finally {
      setOpenEdit(false);
    }
  };

  const columns = [
    { field: "stateName", headerName: "State Name", flex: 1 },
    { field: "isActive", headerName: "Is Active", flex: 1, type: "boolean" },
    { field: "updatedBy", headerName: "Updated By", flex: 1 },
    {
      field: "updatedAt",
      headerName: "Updated At",
      flex: 1,
      type: "date",
      valueFormatter: (params) => {
        const date = new Date(params.value);
        return date.toLocaleDateString(); // Formats to show only the date
      }
    },
    { field: "createdBy", headerName: "Created By", flex: 1 },
    {
      field: "createdAt",
      headerName: "Created At",
      flex: 1,
      type: "date",
      valueFormatter: (params) => {
        const date = new Date(params.value);
        return date.toLocaleDateString(); // Formats to show only the date
      }
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        return (
          <Box>
            <IconButton onClick={() => handleView(params.row)}>
              <VisibilityIcon style={{ color: colors.greenAccent[500] }} />
            </IconButton>
            <IconButton onClick={() => handleEdit(params.row)}>
              <EditIcon style={{ color: colors.blueAccent[600] }} />
            </IconButton>
            <IconButton onClick={() => handleDeleteClick(params.row)}>
              <DeleteIcon style={{ color: colors.redAccent[600] }} />
            </IconButton>
          </Box>
        );
      },
    },
  ];


  return (
    <Box m="4px">
      <Header title="State Management" subtitle="List of States" />
      <Box
        m="20px 0 0 0"
        height="68vh"
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[700],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[700],
          },
          "& .MuiCheckbox-root": {
            color: `${colors.greenAccent[200]} !important`,
          },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${colors.grey[100]} !important`,
          },
        }}
      >
        <Box display="flex" justifyContent="flex-end" mb={2}>
          <Button
            variant="contained"
            color="primary"
            sx={{ marginTop: "5px", marginRight: "30px" }}
            onClick={() => setOpenAdd(true)}
          >
            Add State
          </Button>
        </Box>

        <DataGrid
          rows={rows}
          columns={columns}
          components={{ Toolbar: GridToolbar }}
          getRowId={(row) => row.stateId}

        />

        <Dialog open={open} onClose={handleClose}>
          <DialogTitle>State Management</DialogTitle>
          <DialogContent>
            {selectedRow && (
              <>
                <Typography><strong>State ID:</strong> {selectedRow.stateId}</Typography>
                <Typography><strong>State Name:</strong> {selectedRow.stateName}</Typography>
                <Typography><strong>Is Active:</strong> {selectedRow.isActive ? "Yes" : "No"}</Typography>
                <Typography><strong>Updated At:</strong> {selectedRow.updatedAt}</Typography>
                <Typography><strong>Updated By:</strong> {selectedRow.updatedBy}</Typography>
                <Typography><strong>Created At:</strong> {selectedRow.createdAt}</Typography>
                <Typography><strong>Created By:</strong> {selectedRow.createdBy}</Typography>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Close</Button>
          </DialogActions>
        </Dialog>




        {/* View Row Dialog */}
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
          <DialogTitle>State Management</DialogTitle>
          <DialogContent>
            {selectedRow && (
              <>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" color="textSecondary">
                    <strong>State ID:</strong>
                  </Typography>
                  <Typography variant="body1">{selectedRow.stateId}</Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" color="textSecondary">
                    <strong>State Name:</strong>
                  </Typography>
                  <Typography variant="body1">{selectedRow.stateName}</Typography>
                </Box>


                <Divider sx={{ my: 2 }} />

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" color="textSecondary">
                    <strong>Created By:</strong>
                  </Typography>
                  <Typography variant="body1">{selectedRow.createdBy}</Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" color="textSecondary">
                    <strong>Updated By:</strong>
                  </Typography>
                  <Typography variant="body1">{selectedRow.updatedBy}</Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1" color="textSecondary" sx={{ mr: 1 }}>
                    <strong>Is Active:</strong>
                  </Typography>
                  {selectedRow.isActive ? (
                    <CheckCircle color="success" />
                  ) : (
                    <Cancel color="error" />
                  )}
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" color="textSecondary">
                    <strong>Created At:</strong>
                  </Typography>
                  <Typography variant="body1">{new Date(selectedRow.createdAt).toLocaleString()}</Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" color="textSecondary">
                    <strong>Updated At:</strong>
                  </Typography>
                  <Typography variant="body1">{new Date(selectedRow.updatedAt).toLocaleString()}</Typography>
                </Box>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} color="primary" variant="contained">
              Close
            </Button>
          </DialogActions>
        </Dialog>









        <Dialog open={openEdit} onClose={handleCloseEdit} maxWidth="sm" fullWidth>
          <DialogTitle style={{ fontWeight: 'bold', fontSize: '1.5rem', color: '#4a90e2' }}>
            Edit State Group
          </DialogTitle>
          <DialogContent style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '15px' }}>
            {selectedRow && (
              <>
                <TextField
                  autoFocus
                  margin="dense"
                  label="State Name"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={selectedRow.stateName}
                  onChange={(e) => setSelectedRow({ ...selectedRow, stateName: e.target.value })}
                />
              </>
            )}
          </DialogContent>
          <DialogActions style={{ padding: '15px' }}>
            <Button onClick={handleCloseEdit} style={{ color: '#ff6b6b' }}>Cancel</Button>
            <Button onClick={handleSaveEdit} variant="contained" style={{ backgroundColor: '#4a90e2', color: '#fff' }}>
              Save
            </Button>
          </DialogActions>
        </Dialog>





        <Dialog
          open={openAdd}
          onClose={handleCloseAdd}
          PaperProps={{
            sx: {
              width: "600px",
              maxWidth: "600px",
            },
          }}
        >
          <DialogTitle>Add State</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="State Name"
              type="text"
              fullWidth
              variant="outlined"
              value={newState.stateName}
              onChange={(e) => setNewState({ ...newState, stateName: e.target.value })}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCloseAdd}
              sx={{ backgroundColor: 'red', color: 'white', '&:hover': { backgroundColor: 'darkred' } }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveAdd}
              sx={{ backgroundColor: 'green', color: 'white', '&:hover': { backgroundColor: 'darkgreen' } }}
            >
              Add
            </Button>

          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={openDelete} onClose={handleCloseDelete}>
          <DialogTitle>Confirm Deletion</DialogTitle>
          <DialogContent>

            <Typography variant="body1" color="error" style={{ marginTop: '16px' }}>
              Are you sure you want to delete this state?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDelete}>Cancel</Button>
            <Button onClick={handleDelete} color="error">Delete</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default Statedata;
